/** 目標・ゴール（期間 × 領域）のRepository。 */

import { getDb } from './schema';
import { enqueue } from './outbox';
import { bumpData } from './events';
import { getDeviceId, newId } from '../domain/ids';
import { nowIsoUtc } from '../lib/datetime';
import {
  cellKey,
  DEFAULT_GOAL_AREAS,
  SCHEMA_VERSION,
  type GoalArea,
  type GoalsDoc,
  type Horizon,
} from '../domain/types';

const KEY = 'goals';

export function emptyGoals(): GoalsDoc {
  return {
    schemaVersion: SCHEMA_VERSION,
    updatedAt: nowIsoUtc(),
    deviceId: getDeviceId(),
    areas: DEFAULT_GOAL_AREAS.map((a) => ({ ...a })),
    cells: {},
  };
}

export async function getGoals(): Promise<GoalsDoc> {
  const db = await getDb();
  const rec = await db.get('meta', KEY);
  return (rec?.value as GoalsDoc | undefined) ?? emptyGoals();
}

async function save(doc: GoalsDoc): Promise<GoalsDoc> {
  const db = await getDb();
  const next: GoalsDoc = { ...doc, updatedAt: nowIsoUtc(), deviceId: getDeviceId() };
  await db.put('meta', { key: KEY, value: next });
  await enqueue({ type: 'goals.put', targetKey: 'goals' });
  bumpData();
  return next;
}

/** 同期で受け取った内容をそのまま反映する（outboxには積まない）。 */
export async function putGoalsFromRemote(doc: GoalsDoc): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key: KEY, value: doc });
  bumpData();
}

export async function setCell(h: Horizon, areaId: string, text: string): Promise<void> {
  const doc = await getGoals();
  await save({
    ...doc,
    cells: { ...doc.cells, [cellKey(h, areaId)]: { text, updatedAt: nowIsoUtc() } },
  });
}

export async function addArea(name: string): Promise<void> {
  const doc = await getGoals();
  const order = Math.max(0, ...doc.areas.map((a) => a.order)) + 1;
  await save({
    ...doc,
    areas: [...doc.areas, { id: newId(), name, order, deleted: false }],
  });
}

export async function renameArea(id: string, name: string): Promise<void> {
  const doc = await getGoals();
  await save({ ...doc, areas: doc.areas.map((a) => (a.id === id ? { ...a, name } : a)) });
}

/** 論理削除。書いた内容は cells に残るため、復元すれば戻る。 */
export async function removeArea(id: string): Promise<void> {
  const doc = await getGoals();
  await save({ ...doc, areas: doc.areas.map((a) => (a.id === id ? { ...a, deleted: true } : a)) });
}

export async function restoreArea(id: string): Promise<void> {
  const doc = await getGoals();
  await save({ ...doc, areas: doc.areas.map((a) => (a.id === id ? { ...a, deleted: false } : a)) });
}

export async function moveArea(id: string, delta: number): Promise<void> {
  const doc = await getGoals();
  const active = doc.areas.filter((a) => !a.deleted).sort((a, b) => a.order - b.order);
  const i = active.findIndex((a) => a.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= active.length) return;
  [active[i], active[j]] = [active[j], active[i]];
  const orderById = new Map(active.map((a, n) => [a.id, n + 1]));
  await save({
    ...doc,
    areas: doc.areas.map((a) => (orderById.has(a.id) ? { ...a, order: orderById.get(a.id)! } : a)),
  });
}

export function activeAreas(doc: GoalsDoc): GoalArea[] {
  return doc.areas.filter((a) => !a.deleted).sort((a, b) => a.order - b.order);
}
